import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './PushNotify.module.css';

const WORKER_URL = process.env.REACT_APP_PUSH_WORKER_URL;
const VAPID_KEY  = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const PLATFORM_OPTIONS = [
  { id: 'epic',    label: 'Epic Games' },
  { id: 'steam',   label: 'Steam' },
  { id: 'gog',     label: 'GOG' },
  { id: 'itchio',  label: 'Itch.io' },
];

const ALL_CATEGORIES = PLATFORM_OPTIONS.map(p => p.id);
const LS_CATEGORIES_KEY = 'gamedrop_notify_categories';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function PushNotify() {
  const [status, setStatus]         = useState('idle'); // idle | configuring | loading | subscribed | managing | error
  const [permDenied, setPermDenied] = useState(false);
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_CATEGORIES_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return ALL_CATEGORIES;
  });
  const panelRef    = useRef(null);
  const userEditedRef = useRef(false); // true once user manually toggles a checkbox

  // Check if already subscribed on mount; sync categories from Worker (source of truth)
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !WORKER_URL || !VAPID_KEY) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      // If user already opened the panel (configuring), keep it open as managing instead of closing it
      setStatus(prev => prev === 'configuring' ? 'managing' : 'subscribed');
      // Fetch categories from Worker — only update state if the user hasn't manually changed checkboxes
      try {
        const res = await fetch(`${WORKER_URL}/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.found && Array.isArray(data.categories) && data.categories.length > 0) {
            localStorage.setItem(LS_CATEGORIES_KEY, JSON.stringify(data.categories));
            // Only update state if the user hasn't already manually changed checkboxes
            if (!userEditedRef.current) {
              setCategories(data.categories);
            }
          }
        }
      } catch {}
    }).catch(() => {});
  }, []);

  // Close panel on outside click
  useEffect(() => {
    if (status !== 'configuring' && status !== 'managing') return;
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setStatus('idle');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [status]);

  const toggleCategory = useCallback((id) => {
    userEditedRef.current = true;
    setCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  }, []);

  const subscribe = useCallback(async () => {
    if (!WORKER_URL || !VAPID_KEY) return;
    if (categories.length === 0) return; // must pick at least one
    // Save preference immediately before any async work that could fail
    localStorage.setItem(LS_CATEGORIES_KEY, JSON.stringify(categories));
    userEditedRef.current = false; // reset so next mount sync can run
    setStatus('loading');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPermDenied(true);
        setStatus('idle');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });
      await fetch(`${WORKER_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription, categories }),
      });
      setStatus('subscribed');
    } catch (err) {
      console.error('[PushNotify] Subscribe failed:', err);
      setStatus('error');
    }
  }, [categories]);

  const unsubscribe = useCallback(async () => {
    setStatus('loading');
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await fetch(`${WORKER_URL}/subscribe`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription }),
        });
        await subscription.unsubscribe();
      }
      // Keep category preference in localStorage so it's remembered next time
      setStatus('idle');
    } catch (err) {
      console.error('[PushNotify] Unsubscribe failed:', err);
      setStatus('error');
    }
  }, []);

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null; // browser doesn't support push at all
  }

  const isNotConfigured = !WORKER_URL || !VAPID_KEY;
  const isSubscribed  = status === 'subscribed';
  const isLoading     = status === 'loading';
  const isConfiguring = status === 'configuring';
  const isManaging    = status === 'managing';

  return (
    <div className={styles.wrap} ref={panelRef}>
      {permDenied && (
        <span className={styles.hint}>Notifications blocked — enable in browser settings.</span>
      )}

      {/* Main bell button */}
      <button
        className={`${styles.btn} ${isSubscribed ? styles.subscribed : ''}`}
        onClick={() => {
          if (isManaging || isConfiguring) setStatus('idle');
          else if (isSubscribed) setStatus('managing');
          else setStatus('configuring');
        }}
        disabled={isLoading}
        title={isNotConfigured ? 'Push notifications require Worker setup (see cloudflare-worker/)' : isSubscribed ? 'Manage deal alerts' : 'Get notified when free games drop'}
        aria-label={isSubscribed ? 'Manage push notifications' : 'Enable push notifications'}
        aria-expanded={isConfiguring || isManaging}
      >
        {isLoading ? <span className={styles.spinner} /> : <BellIcon subscribed={isSubscribed} />}
        <span className={styles.label}>
          {isLoading ? 'Saving…' : isSubscribed ? 'Alerts On' : 'Notify Me'}
        </span>
      </button>

      {/* Category selection / management panel */}
      {(isConfiguring || isManaging) && (
        <div className={styles.panel} role="dialog" aria-label="Choose notification categories">
          {isNotConfigured ? (
            <>
              <p className={styles.panelTitle}>Setup required</p>
              <p className={styles.panelSetup}>
                Deploy the Cloudflare Worker and set<br />
                <code>REACT_APP_PUSH_WORKER_URL</code><br />
                <code>REACT_APP_VAPID_PUBLIC_KEY</code><br />
                to enable push notifications.
              </p>
            </>
          ) : (
            <>
              <p className={styles.panelTitle}>Notify me about:</p>
              <div className={styles.checkboxList}>
                {PLATFORM_OPTIONS.map(opt => (
                  <label key={opt.id} className={styles.checkLabel}>
                    <input
                      type="checkbox"
                      className={styles.check}
                      checked={categories.includes(opt.id)}
                      onChange={() => toggleCategory(opt.id)}
                    />
                    <span className={styles.checkText}>{opt.label}</span>
                  </label>
                ))}
              </div>
              <button
                className={styles.confirmBtn}
                onClick={subscribe}
                disabled={categories.length === 0}
              >
                {categories.length === 0 ? 'Select at least one' : isManaging ? 'Update Alerts' : 'Enable Alerts'}
              </button>
              {isManaging && (
                <button className={styles.turnOffBtn} onClick={unsubscribe}>
                  Turn Off
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BellIcon({ subscribed }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {subscribed
        ? <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
        : <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
      }
    </svg>
  );
}

export { PushNotify };
export default PushNotify;
